<template>
    <div class="main-content">
        <!-- TOASTS DE ALERTA -->
        <div class="toast-container position-fixed top-0 end-0 p-3" style="z-index: 1060">
            <transition-group name="slide-fade">
                <div v-for="alert in alerts" :key="alert.id"
                    class="toast show align-items-center border-0 shadow-lg mb-2"
                    :class="'bg-' + alert.type + '-subtle text-' + alert.type" role="alert">
                    <div class="d-flex p-3">
                        <i :class="alert.icon" class="me-3 fs-5"></i>
                        <div class="toast-body p-0 fw-medium">
                            {{ alert.message }}
                        </div>
                        <button type="button" class="btn-close ms-auto" @click="removeAlert(alert.id)"></button>
                    </div>
                </div>
            </transition-group>
        </div>

        <header class="content-header mb-4 mt-n5">
            <nav aria-label="breadcrumb">
                <ol class="breadcrumb mb-0">
                    <li class="breadcrumb-item"><router-link class="nav-link active"
                            to="/dashboard">Início</router-link></li>
                    <li class="breadcrumb-item active text-dark small">Financeiro - Saques</li>
                </ol>
            </nav>
        </header>

        <!-- CARDS DE RESUMO FINANCEIRO (3 COLUNAS) -->
        <div class="row g-3 mb-4">
            <!-- Card 1: Saldo Disponível -->
            <div class="col-12 col-md-4">
                <div
                    class="card p-4 h-100 border-0 shadow-subtle rounded-4 bg-success bg-opacity-10 border border-success-subtle">
                    <div class="d-flex align-items-center text-success mb-2">
                        <i class="bi bi-wallet2 fs-4 me-2"></i>
                        <span class="fw-bold">Saldo Disponível</span>
                    </div>
                    <h3 class="fw-bold text-success mb-0">R$ {{ formatCurrency(availableBalance) }}</h3>
                    <small class="text-success opacity-75 mt-2">Liberado para transferência</small>
                    <transition name="fade">
                        <div v-if="pendingReleaseBalance > 0"
                            class="mt-3 pt-3 border-top border-success border-opacity-25">
                            <div class="d-flex align-items-center text-success">
                                <i class="bi bi-clock-history me-2 fs-5"></i>
                                <div class="lh-sm">
                                    <strong class="d-block" style="font-size: 0.95rem;">
                                        R$ {{ formatCurrency(pendingReleaseBalance) }}
                                    </strong>
                                    <span class="opacity-75" style="font-size: 0.75rem;">
                                        pendente de liberação (Cartão)
                                    </span>
                                </div>
                            </div>
                        </div>
                    </transition>
                </div>
            </div>

            <!-- Card 2: Total Já Sacado (Valores Pagos) -->
            <div class="col-12 col-md-4">
                <!-- Card Azul (Primary) -->
                <div
                    class="card p-4 h-100 border-0 shadow-subtle rounded-4 bg-primary bg-opacity-10 border border-primary-subtle">

                    <div class="d-flex align-items-center text-primary mb-2">
                        <i class="bi bi-check-circle fs-4 me-2"></i>
                        <span class="fw-bold">Total Já Sacado</span>
                    </div>

                    <h3 class="fw-bold text-primary mb-0">R$ {{ formatCurrency(totalWithdrawn) }}</h3>
                    <small class="text-primary opacity-75 mt-2">Transferidos com sucesso</small>

                    <!-- 👇 NOVA SEÇÃO: SAQUE PENDENTE / EM PROCESSAMENTO -->
                    <transition name="fade">
                        <div v-if="totalPending > 0" class="mt-3 pt-3 border-top border-primary border-opacity-25">
                            <div class="d-flex align-items-center text-primary">
                                <i class="bi bi-hourglass-split me-2 fs-5"></i>
                                <div class="lh-sm">
                                    <strong class="d-block" style="font-size: 0.95rem;">
                                        R$ {{ formatCurrency(totalPending) }}
                                    </strong>
                                    <span class="opacity-75" style="font-size: 0.75rem;">
                                        saque em processamento
                                    </span>
                                </div>
                            </div>
                        </div>
                    </transition>

                </div>
            </div>
            <!-- Card 3: Ação / Solicitação -->
            <div class="col-12 col-md-4">
                <div class="card p-4 h-100 border-0 shadow-subtle rounded-4 d-flex flex-column justify-content-between">
                    <div>
                        <h6 class="fw-bold mb-1">Transferência PIX</h6>
                        <p class="text-muted small mb-3">Movimente fundos ou gerencie suas contas.</p>
                    </div>
                    <div class="d-flex flex-column gap-2">
                        <button class="btn btn-pmg-primary text-decoration-none"
                            :disabled="hasPendingWithdrawal || loading || availableBalance < 10"
                            @click="openWithdrawalModal">
                            <i class="bi bi-arrow-up-right-circle me-2"></i> Solicitar Saque
                        </button>
                        <button class="btn btn-pmg-info text-decoration-none" @click="openBankAccountsModal">
                            <i class="bi bi-bank me-2"></i> Contas Bancárias
                        </button>
                    </div>
                    <small v-if="hasPendingWithdrawal" class="text-warning fw-medium text-center mt-2"
                        style="font-size: 0.75rem;">
                        <i class="bi bi-exclamation-triangle me-1"></i> Saque pendente em análise.
                    </small>
                </div>
            </div>
        </div>

        <!-- HISTÓRICO DE SAQUES -->
        <div class="card p-4 shadow-subtle rounded-4 border-0">
            <div class="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                <div>
                    <h4 class="mb-0 fw-bold">Histórico de Saques</h4>
                    <span class="text-muted fs-7">Acompanhe suas solicitações</span>
                </div>
                <button class="btn btn-pmg-light btn-sm" @click="fetchWithdrawals" :disabled="loading">
                    <i class="bi bi-arrow-clockwise me-1"></i> Atualizar
                </button>
            </div>

            <div class="table-responsive">
                <table class="table table-pmg align-middle">
                    <thead>
                        <tr>
                            <th style="width: 15%">Data</th>
                            <th style="width: 20%">Valor Solicitado</th>
                            <th style="width: 35%">Conta Destino (PIX)</th>
                            <th style="width: 15%">Status</th>
                            <th style="width: 15%">Pagamento</th>
                        </tr>
                    </thead>
                    <tbody v-if="withdrawals.length > 0">
                        <tr v-for="item in withdrawals" :key="item.id">
                            <td>
                                <div class="fw-medium text-dark">{{ formatDateBr(item.created_at) }}</div>
                                <small class="text-muted">{{ formatTime(item.created_at) }}</small>
                            </td>
                            <td>
                                <div class="fw-bold text-dark fs-6">R$ {{ formatCurrency(item.amount) }}</div>
                            </td>
                            <td>
                                <div class="d-flex flex-column">
                                    <span class="fw-medium text-dark mb-1">
                                        <i class="bi bi-key text-secondary me-1"></i> {{ item.pix_key }}
                                    </span>
                                    <span
                                        class="badge bg-light text-secondary border border-light-subtle rounded-pill align-self-start"
                                        style="font-size: 0.7rem;">
                                        Tipo: {{ formatPixType(item.pix_type) }}
                                    </span>
                                </div>
                            </td>
                            <td>
                                <span class="badge rounded-pill px-3 py-2" :class="getStatusClass(item.status)">
                                    {{ getStatusText(item.status) }}
                                </span>
                            </td>
                            <td>
                                <div v-if="item.processed_at" class="text-success small fw-medium">
                                    <i class="bi bi-check2-all me-1"></i> {{ formatDateBr(item.processed_at) }}
                                </div>
                                <div v-else class="text-muted small">
                                    <i class="bi bi-clock me-1"></i> Aguardando
                                </div>
                            </td>
                        </tr>
                    </tbody>
                    <tbody v-else>
                        <tr>
                            <td colspan="5" class="text-center py-5 text-muted">
                                <div class="d-flex flex-column align-items-center justify-content-center">
                                    <i class="bi bi-inbox fs-1 mb-2 text-secondary opacity-50"></i>
                                    <span class="fw-medium">Nenhum saque solicitado ainda.</span>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- MODAL DE SOLICITAÇÃO DE SAQUE -->
        <div class="modal fade" id="modalWithdrawal" tabindex="-1" aria-hidden="true" data-bs-backdrop="static">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content border-0 shadow-lg rounded-4">
                    <div class="modal-header border-0 pt-4 px-4 pb-2">
                        <h5 class="fw-bold m-0 d-flex align-items-center">
                            <i class="bi bi-cash-stack me-2 text-primary"></i> Solicitar Saque PIX
                        </h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" @click="resetForm"></button>
                    </div>

                    <div class="modal-body px-4 pb-4">
                        <div class="card bg-success bg-opacity-10 border border-success-subtle p-3 rounded-3 mb-3">
                            <div class="d-flex align-items-center justify-content-between">
                                <span class="text-success small fw-bold"><i class="bi bi-wallet2 me-1"></i> Saldo
                                    Disponível:</span>
                                <span class="fs-5 fw-bold text-success">R$ {{ formatCurrency(availableBalance) }}</span>
                            </div>
                        </div>

                        <div class="mb-4">
                            <label class="form-label small fw-bold text-muted">Valor do Saque (R$)</label>
                            <input type="text" class="form-control form-control-pmg fs-5 fw-bold text-success"
                                placeholder="R$ 0,00" :value="displayAmount" @input="handleMoneyInput">
                            <div class="form-text text-danger fw-medium" v-if="form.amount > availableBalance">Valor
                                excede o saldo disponível.</div>
                        </div>

                        <div class="mb-2">
                            <label class="form-label small fw-bold text-muted d-flex justify-content-between">
                                Conta de Destino
                                <!-- Atalho rápido caso não tenha conta -->
                                <a href="#" class="text-primary text-decoration-none" @click.prevent="switchToBankModal"
                                    v-if="bankAccounts.length === 0">Cadastrar conta</a>
                            </label>
                            <select v-model="form.bank_account_id" class="form-select form-control-pmg py-2">
                                <option value="" disabled>Selecione uma conta bancária</option>
                                <option v-for="bank in bankAccounts" :key="bank.id" :value="bank.id">
                                    {{ bank.bank_name || 'Banco' }} - Ag: {{ bank.agency }} | CC: {{ bank.account_number
                                    }}-{{ bank.account_digit }}
                                </option>
                            </select>
                            <div class="form-text text-warning small mt-1" v-if="bankAccounts.length === 0">
                                <i class="bi bi-exclamation-triangle"></i> Você precisa cadastrar uma conta bancária
                                primeiro.
                            </div>
                        </div>
                    </div>

                    <div class="modal-footer border-0 pb-4 px-4 d-flex justify-content-between">
                        <button class="btn btn-pmg-light px-4" data-bs-dismiss="modal"
                            @click="resetForm">Cancelar</button>
                        <button class="btn btn-pmg-primary px-4" :disabled="!isWithdrawalValid || saving"
                            @click="submitWithdrawal">
                            <span v-if="saving" class="spinner-border spinner-border-sm me-2" role="status"></span>
                            <i v-else class="bi bi-check-lg me-1"></i> {{ saving ? 'Processando...' : 'Confirmar Saque'
                            }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <!-- MODAL DE CONTAS BANCÁRIAS -->
        <div class="modal fade" id="modalBankAccounts" tabindex="-1" aria-hidden="true" data-bs-backdrop="static">
            <div class="modal-dialog modal-lg modal-dialog-centered">
                <div class="modal-content border-0 shadow-lg rounded-4">
                    <div class="modal-header border-0 pt-4 px-4 pb-3">
                        <h5 class="fw-bold m-0 d-flex align-items-center">
                            <i class="bi bi-bank me-2 text-primary"></i>
                            {{ showBankForm ? (isEditingBank ? 'Editar Conta' : 'Nova Conta Bancária') : 'Minhas Contas'
                            }}
                        </h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal"
                            @click="closeBankModal"></button>
                    </div>

                    <div class="modal-body px-4 pb-4">

                        <!-- TELA 1: LISTAGEM -->
                        <div v-if="!showBankForm">
                            <div class="d-flex justify-content-between mb-3">
                                <span class="text-muted small">Gerencie as contas para recebimento dos repasses.</span>
                                <button class="btn btn-pmg-primary btn-sm px-3" @click="openBankForm()">
                                    <i class="bi bi-plus-lg me-1"></i> Adicionar Conta
                                </button>
                            </div>

                            <div class="list-group list-group-flush border-top border-bottom"
                                v-if="bankAccounts.length > 0">
                                <div class="list-group-item px-0 py-3 d-flex justify-content-between align-items-center"
                                    v-for="bank in bankAccounts" :key="bank.id">
                                    <div class="d-flex align-items-start gap-3">
                                        <div class="bg-light p-2 rounded-3 text-secondary border"><i
                                                class="bi bi-building"></i></div>
                                        <div>
                                            <div class="fw-bold text-dark">{{ bank.bank_name || 'Instituição Financeira'
                                                }}</div>
                                            <div class="small text-muted mb-1">Ag: {{ bank.agency }} | Conta: {{
                                                bank.account_number }}-{{ bank.account_digit }} ({{ bank.account_type
                                                    === 'checking' ? 'Corrente' : 'Poupança' }})</div>
                                            <span class="badge bg-light border text-secondary"
                                                style="font-size: 0.65rem;">PIX: {{ formatPixType(bank.pix_type) }} - {{
                                                    bank.pix_key }}</span>
                                        </div>
                                    </div>
                                    <div class="dropdown">
                                        <button class="btn btn-pmg-icon border btn-sm" data-bs-toggle="dropdown"><i
                                                class="bi bi-three-dots-vertical"></i></button>
                                        <ul class="dropdown-menu dropdown-menu-end shadow border-0">
                                            <li><a class="dropdown-item" href="#" @click.prevent="openBankForm(bank)"><i
                                                        class="bi bi-pencil me-2"></i>Editar</a></li>
                                            <li>
                                                <hr class="dropdown-divider">
                                            </li>
                                            <li><a class="dropdown-item text-danger" href="#"
                                                    @click.prevent="deleteBankAccount(bank.id)"><i
                                                        class="bi bi-trash me-2"></i>Remover</a></li>
                                        </ul>
                                    </div>
                                </div>
                            </div>

                            <div class="text-center py-5 text-muted" v-else>
                                <i class="bi bi-bank2 fs-2 mb-2 text-secondary opacity-50"></i>
                                <div class="fw-medium">Nenhuma conta cadastrada.</div>
                            </div>
                        </div>

                        <div v-else>
                            <div
                                class="alert bg-warning bg-opacity-10 border border-warning-subtle text-dark d-flex p-3 rounded-3 mb-4">
                                <i class="bi bi-shield-lock-fill fs-5 text-warning me-3 mt-1"></i>
                                <div class="small fw-medium">A conta e a chave PIX devem ser de <strong>mesma
                                        titularidade</strong> dos documentos cadastrados na plataforma.</div>
                            </div>
                            <div class="row g-3">
                                <!-- 1. Campo de Banco com Autocomplete Customizado -->
                                <div class="col-md-6 position-relative">
                                    <label class="form-label small fw-bold text-muted">Banco (Nome ou Código)</label>
                                    <input v-model="bankForm.bank_name" type="text"
                                        class="form-control form-control-pmg" placeholder="Ex: Nubank, Itaú, 341..."
                                        @focus="showBankDropdown = true" @blur="showBankDropdown = false">

                                    <!-- Lista suspensa customizada -->
                                    <ul class="dropdown-menu show w-100 shadow-lg border-0 mt-1"
                                        v-if="showBankDropdown && filteredBanks.length > 0"
                                        style="max-height: 220px; overflow-y: auto; position: absolute; z-index: 1060;">
                                        <li v-for="bank in filteredBanks" :key="bank.code">
                                            <!-- Usamos mousedown.prevent para evitar que o input perca o foco antes do clique registrar -->
                                            <a class="dropdown-item py-2 text-wrap" href="#"
                                                @mousedown.prevent="selectBank(bank)">
                                                <span class="fw-bold text-primary">{{ bank.code }}</span> - {{ bank.name
                                                }}
                                            </a>
                                        </li>
                                    </ul>

                                    <ul class="dropdown-menu show w-100 shadow border-0 mt-1"
                                        v-if="showBankDropdown && filteredBanks.length === 0"
                                        style="position: absolute; z-index: 1060;">
                                        <li><span class="dropdown-item text-muted disabled">Nenhum banco
                                                encontrado</span></li>
                                    </ul>
                                </div>

                                <div class="col-md-6">
                                    <label class="form-label small fw-bold text-muted">Tipo de Conta</label>
                                    <select v-model="bankForm.account_type" class="form-select form-control-pmg">
                                        <option value="checking">Conta Corrente</option>
                                        <option value="savings">Conta Poupança</option>
                                    </select>
                                </div>
                                <div class="col-md-4">
                                    <label class="form-label small fw-bold text-muted">Agência (Sem dígito)</label>
                                    <input v-model="bankForm.agency" type="text" class="form-control form-control-pmg"
                                        placeholder="xxxxx">
                                </div>
                                <div class="col-md-5">
                                    <label class="form-label small fw-bold text-muted">Conta</label>
                                    <input v-model="bankForm.account_number" type="text"
                                        class="form-control form-control-pmg" placeholder="xxxxx">
                                </div>
                                <div class="col-md-3">
                                    <label class="form-label small fw-bold text-muted">Dígito</label>
                                    <input v-model="bankForm.account_digit" type="text"
                                        class="form-control form-control-pmg" placeholder="xxxxx">
                                </div>

                                <div class="col-12 border-top mt-4 pt-3">
                                    <h6 class="fw-bold mb-3">Chave PIX da Conta</h6>
                                </div>

                                <div class="col-md-4">
                                    <label class="form-label small fw-bold text-muted">Tipo de Chave</label>
                                    <select v-model="bankForm.pix_type" class="form-select form-control-pmg"
                                        @change="handleBankPixTypeChange">
                                        <option value="" disabled>Selecione</option>
                                        <option value="cpf">CPF</option>
                                        <option value="cnpj">CNPJ</option>
                                        <option value="phone">Celular</option>
                                        <option value="email">E-mail</option>
                                        <option value="random">Chave Aleatória</option>
                                    </select>
                                </div>
                                <div class="col-md-8">
                                    <label class="form-label small fw-bold text-muted">Chave PIX</label>
                                    <input v-model="bankForm.pix_key" type="text" class="form-control form-control-pmg"
                                        :placeholder="getBankPixPlaceholder()" :maxlength="getBankPixMaxLength()"
                                        @input="handleBankPixKeyInput" :disabled="!bankForm.pix_type">
                                </div>
                            </div>
                        </div>

                    </div>

                    <div class="modal-footer border-0 pb-4 px-4 d-flex justify-content-between" v-if="showBankForm">
                        <button class="btn btn-pmg-light px-4" @click="showBankForm = false">Voltar</button>
                        <button class="btn btn-pmg-primary px-4" :disabled="!isBankFormValid || savingBank"
                            @click="saveBankAccount">
                            <span v-if="savingBank" class="spinner-border spinner-border-sm me-2" role="status"></span>
                            <i v-else class="bi bi-check-lg me-1"></i> Salvar Conta
                        </button>
                    </div>
                </div>
            </div>
        </div>

    </div>
</template>

<script>
import api from '@/services/api';

export default {
    inject: ['globalLoading'],
    data() {
        return {
            withdrawals: [],
            availableBalance: 0,
            pendingReleaseBalance: 0,
            totalWithdrawn: 0,
            totalPending: 0,
            alerts: [],
            loading: false,
            form: { amount: 0, bank_account_id: '' },
            bankAccounts: [],
            modalBankInstance: null,
            displayAmount: '',
            modalInstance: null,
            showBankForm: false,
            isEditingBank: false,
            savingBank: false,
            showBankDropdown: false,
            banksList: [],
            bankForm: {
                id: null,
                bank_name: '',
                agency: '',
                account_number: '',
                account_digit: '',
                account_type: 'checking',
                pix_type: '',
                pix_key: ''
            }
        }
    },
    computed: {
        hasPendingWithdrawal() {
            return this.withdrawals.some(item => item.status === 'pending');
        },
        isFormValid() {
            return this.form.amount >= 10 &&
                this.form.amount <= this.availableBalance &&
                this.form.pix_type !== '' &&
                this.form.pix_key.trim() !== '';
        },
        isWithdrawalValid() {
            return this.form.amount >= 10 &&
                this.form.amount <= this.availableBalance &&
                this.form.bank_account_id !== '';
        },
        isBankFormValid() {
            return this.bankForm.agency &&
                this.bankForm.account_number &&
                this.bankForm.account_digit &&
                this.bankForm.pix_type &&
                this.bankForm.pix_key;
        },
        filteredBanks() {
            const list = this.banksList || [];
            const search = this.bankForm && this.bankForm.bank_name ? String(this.bankForm.bank_name).toLowerCase() : '';

            if (!search) {
                return list;
            }

            return list.filter(bank => {
                const name = bank.name ? String(bank.name).toLowerCase() : '';
                const code = bank.code ? String(bank.code).toLowerCase() : '';
                return name.includes(search) || code.includes(search);
            });
        }
    },
    mounted() {
        const modalElement = document.getElementById('modalWithdrawal');
        if (modalElement) {
            this.modalInstance = new window.bootstrap.Modal(modalElement);
        }
        const modalBankEl = document.getElementById('modalBankAccounts');
        if (modalBankEl) this.modalBankInstance = new window.bootstrap.Modal(modalBankEl);
        this.fetchDashboardData();
        this.fetchBanks();
    },
    methods: {
        async fetchDashboardData() {
            this.loading = true;
            try {
                // Adicionado a chamada para buscar as contas bancárias junto com o resto
                const [balanceRes, listRes, bankRes] = await Promise.all([
                    api.get('/client-jwt/withdrawals/balance'),
                    api.get('/client-jwt/withdrawals'),
                    api.get('/client-jwt/bank-accounts')
                ]);

                const summary = balanceRes.data.data || balanceRes.data;
                this.availableBalance = summary.available_balance || 0;
                this.pendingReleaseBalance = summary.pending_release_balance || 0;
                this.totalWithdrawn = summary.total_withdrawn || 0;
                this.totalPending = parseFloat(summary.total_pending || 0);

                this.withdrawals = listRes.data.data || [];
                this.bankAccounts = bankRes.data.data || [];

            } catch (error) {
                this.showAlert('Falha ao carregar os dados financeiros.', 'error');
            } finally {
                this.loading = false;
            }
        },

        async fetchWithdrawals() {
            this.fetchDashboardData();
        },

        openWithdrawalModal() {
            if (this.hasPendingWithdrawal) {
                this.showAlert('Você já possui uma solicitação de saque pendente.', 'warning');
                return;
            }

            this.resetForm();
            if (this.modalInstance) {
                this.modalInstance.show();
            }
        },

        resetForm() {
            this.form = { amount: 0, pix_type: '', pix_key: '' };
            this.displayAmount = '';
        },

        handleMoneyInput(event) {
            let digits = event.target.value.replace(/\D/g, '');

            if (!digits) {
                this.form.amount = 0;
                this.displayAmount = '';
                event.target.value = '';
                return;
            }

            const floatValue = parseFloat(digits) / 100;
            this.form.amount = floatValue;

            const formatted = floatValue.toLocaleString('pt-BR', {
                style: 'currency',
                currency: 'BRL'
            });

            this.displayAmount = formatted;
            event.target.value = formatted;
        },

        async submitWithdrawal() {
            this.saving = true;
            try {
                const response = await api.post('/client-jwt/withdrawals', this.form);

                const newWithdrawal = response.data.data;
                this.withdrawals.unshift(newWithdrawal);
                this.availableBalance -= this.form.amount;
                this.showAlert(response.data.message || 'Saque solicitado com sucesso!');
                if (this.modalInstance) {
                    this.modalInstance.hide();
                }
                this.resetForm();

            } catch (error) {
                const errorMsg = error.response?.data?.message || 'Erro ao processar o saque.';
                this.showAlert(errorMsg, 'error');
            } finally {
                this.saving = false;
            }
        },

        formatCurrency(value) {
            if (!value) return '0,00';
            return parseFloat(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },

        formatDateBr(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString);
            return date.toLocaleDateString('pt-BR');
        },

        formatTime(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString);
            return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
        },

        formatPixType(type) {
            const types = {
                'cpf': 'CPF',
                'cnpj': 'CNPJ',
                'phone': 'Celular',
                'email': 'E-mail',
                'random': 'Chave Aleatória'
            };
            return types[type] || type;
        },

        getStatusText(status) {
            const statuses = {
                'pending': 'Pendente',
                'processing': 'Em Processamento',
                'completed': 'Concluído',
                'rejected': 'Rejeitado'
            };
            return statuses[status] || status;
        },

        getStatusClass(status) {
            const classes = {
                'pending': 'bg-warning-subtle text-warning border border-warning-subtle',
                'processing': 'bg-info-subtle text-info border border-info-subtle',
                'completed': 'bg-success-subtle text-success border border-success-subtle',
                'rejected': 'bg-danger-subtle text-danger border border-danger-subtle'
            };
            return classes[status] || 'bg-light text-dark';
        },

        // --- ALERTAS (TOASTS) ---
        showAlert(message, type = 'success') {
            const id = Date.now();
            let icon = 'bi-check-circle-fill';
            if (type === 'danger' || type === 'error') icon = 'bi-exclamation-circle-fill';
            if (type === 'warning') icon = 'bi-exclamation-triangle-fill';

            this.alerts.push({ id, message, type: type === 'error' ? 'danger' : type, icon });
            setTimeout(() => this.removeAlert(id), 5000);
        },
        removeAlert(id) {
            this.alerts = this.alerts.filter(a => a.id !== id);
        },
        handlePixTypeChange() {
            this.form.pix_key = '';
        },

        handlePixKeyInput(event) {
            let value = event.target.value;
            const type = this.form.pix_type;

            if (type === 'cpf') {
                value = value.replace(/\D/g, '').substring(0, 11);
                value = value.replace(/(\d{3})(\d)/, '$1.$2');
                value = value.replace(/(\d{3})(\d)/, '$1.$2');
                value = value.replace(/(\d{3})(\d{1,2})$/, '$1-$2');

            } else if (type === 'cnpj') {
                value = value.replace(/\D/g, '').substring(0, 14);
                value = value.replace(/^(\d{2})(\d)/, '$1.$2');
                value = value.replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3');
                value = value.replace(/\.(\d{3})(\d)/, '.$1/$2');
                value = value.replace(/(\d{4})(\d)/, '$1-$2');

            } else if (type === 'phone') {
                value = value.replace(/\D/g, '').substring(0, 11);
                value = value.replace(/^(\d{2})(\d)/g, '($1) $2');
                value = value.replace(/(\d)(\d{4})$/, '$1-$2');
            }
            this.form.pix_key = value;
            event.target.value = value;
        },

        getPixPlaceholder() {
            const placeholders = {
                'cpf': '000.000.000-00',
                'cnpj': '00.000.000/0000-00',
                'phone': '(00) 00000-0000',
                'email': 'exemplo@email.com',
                'random': '00000000-0000-0000-0000-000000000000'
            };
            return placeholders[this.form.pix_type] || 'Informe a chave PIX';
        },

        getPixMaxLength() {
            const lengths = {
                'cpf': 14,
                'cnpj': 18,
                'phone': 15,
                'random': 36
            };
            return lengths[this.form.pix_type] || 255;
        },
        openBankAccountsModal() {
            this.showBankForm = false;
            if (this.modalBankInstance) this.modalBankInstance.show();
        },
        closeBankModal() {
            this.showBankForm = false;
            this.resetBankForm();
        },
        switchToBankModal() {
            // Se estiver no modal de saque e não tiver conta, troca de modal
            if (this.modalInstance) this.modalInstance.hide();
            this.openBankAccountsModal();
        },

        // --- MÉTODOS DA CONTA BANCÁRIA ---
        openBankForm(bank = null) {
            if (bank) {
                this.isEditingBank = true;
                this.bankForm = { ...bank };
            } else {
                this.isEditingBank = false;
                this.resetBankForm();
            }
            this.showBankForm = true;
        },
        resetBankForm() {
            this.bankForm = { id: null, bank_name: '', agency: '', account_number: '', account_digit: '', account_type: 'checking', pix_type: '', pix_key: '' };
        },
        async saveBankAccount() {
            this.savingBank = true;
            try {
                if (this.isEditingBank) {
                    const res = await api.put(`/client-jwt/bank-accounts/${this.bankForm.id}`, this.bankForm);
                    const index = this.bankAccounts.findIndex(b => b.id === this.bankForm.id);
                    if (index !== -1) this.bankAccounts[index] = res.data.data;
                    this.showAlert('Conta atualizada com sucesso!');
                } else {
                    const res = await api.post('/client-jwt/bank-accounts', this.bankForm);
                    this.bankAccounts.push(res.data.data);
                    this.showAlert('Conta adicionada com sucesso!');
                }
                this.showBankForm = false;
            } catch (error) {
                this.showAlert('Erro ao salvar conta.', 'error');
            } finally {
                this.savingBank = false;
            }
        },
        async deleteBankAccount(id) {
            if (!confirm('Deseja realmente remover esta conta?')) return;
            try {
                await api.delete(`/client-jwt/bank-accounts/${id}`);
                this.bankAccounts = this.bankAccounts.filter(b => b.id !== id);
                this.showAlert('Conta removida.');
            } catch (error) {
                this.showAlert('Erro ao remover conta.', 'error');
            }
        },

        handleBankPixTypeChange() {
            this.bankForm.pix_key = ''; // Limpa se trocar o tipo
        },

        handleBankPixKeyInput(event) {
            let value = event.target.value;
            const type = this.bankForm.pix_type;

            if (type === 'cpf') {
                value = value.replace(/\D/g, '').substring(0, 11);
                value = value.replace(/(\d{3})(\d)/, '$1.$2');
                value = value.replace(/(\d{3})(\d)/, '$1.$2');
                value = value.replace(/(\d{3})(\d{1,2})$/, '$1-$2');

            } else if (type === 'cnpj') {
                value = value.replace(/\D/g, '').substring(0, 14);
                value = value.replace(/^(\d{2})(\d)/, '$1.$2');
                value = value.replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3');
                value = value.replace(/\.(\d{3})(\d)/, '.$1/$2');
                value = value.replace(/(\d{4})(\d)/, '$1-$2');

            } else if (type === 'phone') {
                value = value.replace(/\D/g, '').substring(0, 11);
                value = value.replace(/^(\d{2})(\d)/g, '($1) $2');
                value = value.replace(/(\d)(\d{4})$/, '$1-$2');
            }

            this.bankForm.pix_key = value;
            event.target.value = value;
        },

        getBankPixPlaceholder() {
            const placeholders = {
                'cpf': '000.000.000-00',
                'cnpj': '00.000.000/0000-00',
                'phone': '(00) 00000-0000',
                'email': 'exemplo@email.com',
                'random': '00000000-0000-0000-0000-000000000000'
            };
            return placeholders[this.bankForm.pix_type] || 'Informe a chave PIX';
        },

        getBankPixMaxLength() {
            const lengths = { 'cpf': 14, 'cnpj': 18, 'phone': 15, 'random': 36 };
            return lengths[this.bankForm.pix_type] || 255;
        },
        async fetchBanks() {
            try {
                const response = await fetch('https://brasilapi.com.br/api/banks/v1');
                const banks = await response.json();
                this.banksList = banks
                    .filter(bank => bank.code)
                    .map(bank => ({
                        code: String(bank.code).padStart(3, '0'),
                        name: bank.fullName || bank.name
                    }))
                    .sort((a, b) => a.name.localeCompare(b.name));

            } catch (error) {
                console.error('Erro ao buscar bancos da Brasil API:', error);
                this.banksList = [
                    { code: '001', name: 'Banco do Brasil S.A.' },
                    { code: '033', name: 'Banco Santander (Brasil) S.A.' },
                    { code: '104', name: 'Caixa Econômica Federal' },
                    { code: '237', name: 'Banco Bradesco S.A.' },
                    { code: '341', name: 'Itaú Unibanco S.A.' },
                    { code: '260', name: 'Nubank (Nu Pagamentos S.A.)' },
                    { code: '077', name: 'Banco Inter S.A.' },
                    { code: '380', name: 'PicPay Instituição de Pagamento S.A.' },
                    { code: '336', name: 'Banco C6 S.A.' },
                    { code: '323', name: 'Mercado Pago' },
                    { code: '041', name: 'Banrisul' },
                    { code: '748', name: 'Sicredi' },
                    { code: '756', name: 'Sicoob' }
                ];
            }
        },
        selectBank(bank) {
            this.bankForm.bank_name = `${bank.code} - ${bank.name}`;
            this.showBankDropdown = false;
        },
    }
}
</script>

<style scoped>
.toast {
    min-width: 300px;
    border-radius: 12px;
    background-color: white !important;
}

.main-content {
    background-color: #f8fafc;
    min-height: 100vh;
    padding: 2rem;
    font-family: 'Inter', sans-serif;
}

.card {
    border-radius: 16px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.02);
}

.btn-pmg-primary {
    background: #561569;
    color: #fff;
    border: none;
    border-radius: 10px;
    padding: 10px 20px;
    font-weight: 500;
}

.btn-pmg-primary:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.btn-pmg-light {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    color: #64748b;
    padding: 10px 20px;
}

.form-control-pmg {
    border-radius: 10px;
    border: 1px solid #e2e8f0;
    padding: 12px;
}

.form-control-pmg:focus {
    border-color: #561569;
    box-shadow: 0 0 0 4px rgba(33, 51, 97, 0.1);
}

.table-pmg thead th {
    background: #f8fafc;
    color: #64748b;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    padding: 1.2rem;
    border-bottom: 1px solid #edf2f7;
}

.table-pmg tbody td {
    padding: 1.2rem;
    border-bottom: 1px solid #f1f5f9;
}

.bg-success-subtle {
    background-color: #ecfdf5 !important;
    color: #059669 !important;
}

.bg-warning-subtle {
    background-color: #fffbeb !important;
    color: #d97706 !important;
}

.bg-info-subtle {
    background-color: #eff6ff !important;
    color: #2563eb !important;
}

.bg-danger-subtle {
    background-color: #fef2f2 !important;
    color: #dc2626 !important;
}

.slide-fade-enter-active,
.slide-fade-leave-active {
    transition: all 0.4s ease;
}

.slide-fade-enter-from {
    transform: translateX(100px);
    opacity: 0;
}

.slide-fade-leave-to {
    opacity: 0;
    transform: scale(0.9);
}
</style>